import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { useTenant } from '@/contexts/TenantContext';
import { getDisplayPrice } from '@/utils/pricing';

/**
 * Pulls top manufacturers from product catalog + a small "Bestsellers" row (first N drivers).
 * Fully decorative/merchandising — no side effects on data.
 */
export default function FeaturedBrandsAndBestsellers() {
  const { tenantPath } = useTenant();
  const [brands, setBrands] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [driversRes, shaftsRes] = await Promise.all([
          api.get('/products?category=drivers'),
          api.get('/products?category=shafts'),
        ]);
        const all = [...(driversRes.data || []), ...(shaftsRes.data || [])];
        const counts = {};
        for (const p of all) {
          if (p.manufacturer) counts[p.manufacturer] = (counts[p.manufacturer] || 0) + 1;
        }
        const topBrands = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([name]) => name);
        setBrands(topBrands);
        setBestsellers((driversRes.data || []).slice(0, 4));
      } catch {
        /* swallow */
      }
    })();
  }, []);

  if (brands.length === 0 && bestsellers.length === 0) return null;

  return (
    <>
      {brands.length > 0 && (
        <section className="py-10 bg-white border-y border-gray-100" data-testid="featured-brands">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-xs font-bold uppercase tracking-widest text-gray-500 mb-4">
              Featured Brands
            </p>
            <div className="flex flex-wrap justify-center items-center gap-4">
              {brands.map((b) => (
                <Link
                  key={b}
                  to={tenantPath(`/category/drivers?brand=${encodeURIComponent(b)}`)}
                  className="px-5 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:border-[#334155] hover:bg-gray-50 transition-all"
                  data-testid={`featured-brand-${b.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {b}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {bestsellers.length > 0 && (
        <section className="py-20 px-6 bg-[#FAFAF8]" data-testid="bestsellers">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#334155]">
                  Bestsellers
                </p>
                <h2
                  className="text-3xl md:text-4xl font-bold text-gray-900 mt-1"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  Popular Drivers
                </h2>
              </div>
              <Link
                to={tenantPath('/category/drivers')}
                className="text-sm font-semibold text-[#334155] hover:underline"
              >
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {bestsellers.map((p) => (
                <Link
                  key={p.product_id}
                  to={tenantPath(`/product/${p.product_id}`)}
                  className="group bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-300 hover:shadow-md transition-all hover:-translate-y-1"
                  data-testid={`bestseller-${p.product_id}`}
                >
                  <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden mb-3">
                    {p.image_url && (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#334155]">
                    {p.manufacturer}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5">
                    {p.name}
                  </h3>
                  <p className="text-lg font-bold text-[#334155] mt-1">
                    ${getDisplayPrice(p).toFixed(2)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
